import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Search } from 'lucide-react';
import SearchModal from './SearchModal';
import MarketingMarketSelect from './MarketingMarketSelect';
import useOperatingMarkets from '../../hooks/useOperatingMarkets';
import { marketCountryCode } from '../../utils/marketOptions';
import { siteSettingsService } from '../../services/siteSettingsService';
import { pageList, pageText } from '../../content/publicPages';
import { usePublicPage } from '../../hooks/usePublicPage';

const DEFAULT_HERO_IMAGE = '/images/01_-_HERO_1.webp';
const CAROUSEL_INTERVAL_MS = 5500;

/**
 * Peerspace-style home hero:
 * full-bleed photo · brand headline · What + Where search · activity chips.
 */
const HeroSection = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [marketCode, setMarketCode] = useState('');
  const [activeSlide, setActiveSlide] = useState(0);

  const { saved: home, defaults: homeDefaults } = usePublicPage('home');
  const activityChips = pageList(home.activity_chips, homeDefaults.activity_chips);

  const { data: settingsResponse } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });

  const { markets, isLoading: marketsLoading } = useOperatingMarkets({
    bookableOnly: true,
  });

  const marketOptions = useMemo(
    () =>
      markets.map((market) => {
        const code = marketCountryCode(market);
        return {
          value: code,
          label: market.display_name || market.country?.name || code,
        };
      }),
    [markets]
  );

  const selectedMarketLabel = useMemo(() => {
    const match = marketOptions.find((o) => o.value === marketCode);
    return match?.label || '';
  }, [marketOptions, marketCode]);

  useEffect(() => {
    if (!marketCode && marketOptions.length === 1) {
      setMarketCode(marketOptions[0].value);
    }
  }, [marketCode, marketOptions]);

  const heroImages = useMemo(() => {
    const fromPage = pageList(home.hero_images, [])
      .map((item) => (typeof item === 'string' ? item : item?.src))
      .filter(Boolean);
    if (fromPage.length) return fromPage;
    const settings = settingsResponse?.data;
    const fromCarousel = Array.isArray(settings?.home_hero_images)
      ? settings.home_hero_images.filter(Boolean)
      : [];
    if (fromCarousel.length) return fromCarousel;
    if (settings?.home_hero_image) return [settings.home_hero_image];
    return [DEFAULT_HERO_IMAGE];
  }, [home.hero_images, settingsResponse?.data]);

  useEffect(() => {
    setActiveSlide(0);
  }, [heroImages]);

  useEffect(() => {
    if (heroImages.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroImages.length);
    }, CAROUSEL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [heroImages]);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const runSearch = (overrideQuery) => {
    const params = new URLSearchParams();
    const q = (overrideQuery ?? searchQuery).trim();
    if (q) params.append('q', q);
    if (marketCode) {
      params.append('country', marketCode);
      if (selectedMarketLabel) params.append('location', selectedMarketLabel);
    }
    navigate(`/search?${params.toString()}`);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      runSearch();
    }
  };

  return (
    <section 
      className="relative flex min-h-[100svh] items-center justify-center overflow-hidden px-4 pb-36 pt-28 sm:px-6 sm:pb-40"
      style={{ marginTop: 'calc(-4.25rem - 1px)' }}
    >
      <div className="absolute inset-x-0 bottom-0 z-0" style={{ top: '-1px' }}>
        {heroImages.map((src, index) => (
          <img
            key={`${src}-${index}`}
            src={src}
            alt=""
            className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-1000 ease-out ${
              index === activeSlide ? 'opacity-100' : 'opacity-0'
            }`}
            fetchPriority={index === 0 ? 'high' : 'low'}
            aria-hidden={index !== activeSlide}
          />
        ))}
        <div className="absolute inset-0 bg-black/45" aria-hidden="true" />
      </div>

      <div
        className={`relative z-10 w-full max-w-5xl transform transition-all duration-700 ease-out ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        }`}
      >
        <div className="mb-8 text-center sm:mb-10">
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.18em] text-white/70">
            {pageText(home.hero_eyebrow, homeDefaults.hero_eyebrow)}
          </p>
          <h1 className="marketing-hero-title mx-auto max-w-3xl text-m-on-dark">
            {pageText(home.hero_title, homeDefaults.hero_title)}
          </h1>
          <p className="marketing-body-lg mx-auto mt-4 max-w-xl text-white/80">
            {pageText(home.hero_subtitle, homeDefaults.hero_subtitle)}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowSearchModal(true)}
          className="flex w-full items-center gap-3 rounded-2xl bg-white px-5 py-4 shadow-[0_12px_40px_rgba(0,0,0,0.28)] sm:hidden"
        >
          <Search className="h-4 w-4 shrink-0 text-m-ink" />
          <span className="min-w-0 flex-1 truncate text-left text-sm font-normal text-m-ink-muted">
            {searchQuery || selectedMarketLabel
              ? [searchQuery, selectedMarketLabel].filter(Boolean).join(' · ')
              : 'Search vendors'}
          </span>
          <ArrowRight className="h-4 w-4 shrink-0 text-m-ink" />
        </button>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            runSearch();
          }}
          className="relative hidden rounded-2xl bg-white shadow-[0_16px_48px_rgba(0,0,0,0.28)] sm:block"
        >
          <div className="relative z-10 flex items-stretch rounded-2xl">
            <label className="flex min-w-0 flex-[1.3] cursor-text flex-col justify-center px-6 py-4">
              <span className="mb-1 text-[11px] font-medium text-m-ink">
                What are you planning?
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Photo booth, wedding DJ, birthday…"
                className="w-full border-0 bg-transparent p-0 text-sm font-normal text-m-ink placeholder:text-m-ink-faint focus:outline-none focus:ring-0"
              />
            </label>

            <div className="my-4 w-px shrink-0 bg-m-border-light" aria-hidden="true" />

            <div className="relative z-20 flex min-w-0 flex-1 flex-col justify-center px-6 py-4">
              <span className="mb-1 text-[11px] font-medium text-m-ink">Where?</span>
              <MarketingMarketSelect
                value={marketCode}
                onChange={setMarketCode}
                options={marketOptions}
                loading={marketsLoading}
                variant="hero"
              />
            </div>
            
            <div className="flex items-center p-2 pl-0">
              <button
                type="submit"
                className="inline-flex h-12 items-center gap-2 rounded-xl bg-m-ink px-6 text-sm font-medium text-white transition-colors hover:bg-[#1a1a1a]"
              >
                Search
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </form>
          
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2 sm:mt-6">
          {activityChips.map((chip) => (
            <button
              key={chip.label}
              type="button"
              onClick={() => {
                setSearchQuery(chip.q);
                runSearch(chip.q);
              }}
              className="rounded-full border border-white/35 bg-white/10 px-3.5 py-1.5 text-xs font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20"
            >
              {chip.label}
            </button>
          ))}
            </div>
          </div>

        <SearchModal
          isOpen={showSearchModal}
          onClose={() => setShowSearchModal(false)}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        marketCode={marketCode}
        setMarketCode={setMarketCode}
        marketOptions={marketOptions}
        marketsLoading={marketsLoading}
      />
    </section>
  );
};

export default HeroSection;
